/**
 * Storage Service - Firebase Storage Integration
 *
 * Handles image upload, download, and management with Firebase Storage
 * Replaces localStorage for image storage to avoid QuotaExceededError
 */

import { storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject, listAll } from 'firebase/storage';

const MAX_IMAGE_SIZE_MB = parseInt(process.env.REACT_APP_MAX_IMAGE_SIZE_MB) || 5;
const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024;

/**
 * Upload image to Firebase Storage
 * @param {Blob|File|string} imageData - Image as Blob, File, or base64 string
 * @param {string} folder - Storage folder ('user-photos', 'products', etc.)
 * @param {string} filename - Optional custom filename
 * @returns {Promise<string>} Download URL
 */
export const uploadImage = async (imageData, folder = 'user-photos', filename = null) => {
  try {
    // Convert base64 to Blob if needed
    let blob;
    if (typeof imageData === 'string' && imageData.startsWith('data:')) {
      blob = await base64ToBlob(imageData);
    } else {
      blob = imageData;
    }

    // Check file size
    if (blob.size > MAX_IMAGE_SIZE_BYTES) {
      throw new Error(`L'immagine supera il limite di ${MAX_IMAGE_SIZE_MB}MB`);
    }

    // Generate filename if not provided
    const fileName = filename || `${Date.now()}_${Math.random().toString(36).substr(2, 9)}.jpg`;
    const storagePath = `${folder}/${fileName}`;

    console.log(`📤 Uploading image to ${storagePath}...`);

    // Create storage reference
    const storageRef = ref(storage, storagePath);

    // Upload file
    const snapshot = await uploadBytes(storageRef, blob);

    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);

    console.log(`✅ Image uploaded successfully: ${downloadURL}`);

    return downloadURL;

  } catch (error) {
    console.error('❌ Error uploading image:', error);
    throw error;
  }
};

/**
 * Upload user photo for try-on
 * Also compresses the image before upload
 * @param {string} base64Image - User photo in base64
 * @returns {Promise<Object>} Object with URL and metadata
 */
export const uploadUserPhoto = async (base64Image) => {
  try {
    // Photo is already compressed at capture (512px, 0.75) — upload directly
    const downloadURL = await uploadImage(base64Image, 'user-photos');

    // Store metadata in localStorage for quick access
    const metadata = {
      url: downloadURL,
      uploadedAt: Date.now(),
      expiresAt: Date.now() + (24 * 60 * 60 * 1000) // 24 hours
    };

    localStorage.setItem('userPhotoMetadata', JSON.stringify(metadata));

    console.log('✅ User photo uploaded and metadata saved');

    return metadata;

  } catch (error) {
    console.error('❌ Error uploading user photo:', error);
    throw error;
  }
};

/**
 * Upload product image
 * @param {File|Blob|string} imageData - Product image
 * @param {string} productId - Product ID
 * @returns {Promise<string>} Download URL
 */
export const uploadProductImage = async (imageData, productId) => {
  try {
    const filename = `product_${productId}.jpg`;
    return await uploadImage(imageData, 'products', filename);
  } catch (error) {
    console.error('❌ Error uploading product image:', error);
    throw error;
  }
};

/**
 * Delete image from Firebase Storage
 * @param {string} imageUrl - Image download URL
 * @returns {Promise<void>}
 */
export const deleteImage = async (imageUrl) => {
  try {
    // Extract path from URL
    const urlObj = new URL(imageUrl);
    const pathMatch = urlObj.pathname.match(/\/o\/(.+?)\?/);

    if (!pathMatch) {
      throw new Error('Invalid image URL');
    }

    const imagePath = decodeURIComponent(pathMatch[1]);
    const imageRef = ref(storage, imagePath);

    await deleteObject(imageRef);

    console.log(`✅ Image deleted: ${imagePath}`);

  } catch (error) {
    console.error('❌ Error deleting image:', error);
    throw error;
  }
};

/**
 * Get user photo from storage (URL from metadata)
 * @returns {Promise<string|null>} Image URL or null
 */
export const getUserPhoto = async () => {
  try {
    const metadataStr = localStorage.getItem('userPhotoMetadata');

    if (!metadataStr) {
      return null;
    }

    const metadata = JSON.parse(metadataStr);

    // Check if expired (24 hours)
    if (Date.now() > metadata.expiresAt) {
      console.log('⚠️ User photo expired');
      localStorage.removeItem('userPhotoMetadata');
      return null;
    }

    return metadata.url;

  } catch (error) {
    console.error('❌ Error getting user photo:', error);
    return null;
  }
};

/**
 * Clear expired user photos from localStorage metadata
 * @returns {void}
 */
export const clearExpiredPhotos = () => {
  const metadataStr = localStorage.getItem('userPhotoMetadata');

  if (metadataStr) {
    const metadata = JSON.parse(metadataStr);

    if (Date.now() > metadata.expiresAt) {
      localStorage.removeItem('userPhotoMetadata');
      console.log('✅ Expired photo metadata cleared');
    }
  }
};

/**
 * List all files in a folder
 * @param {string} folder - Folder path
 * @returns {Promise<Array>} Array of file URLs
 */
export const listImages = async (folder) => {
  try {
    const folderRef = ref(storage, folder);
    const result = await listAll(folderRef);

    const urls = await Promise.all(
      result.items.map(item => getDownloadURL(item))
    );

    console.log(`📂 Found ${urls.length} images in ${folder}`);
    return urls;

  } catch (error) {
    console.error(`❌ Error listing images in ${folder}:`, error);
    throw error;
  }
};

/**
 * Compress image to reduce file size
 * @param {string} base64Image - Base64 image string
 * @param {number} maxWidth - Maximum width in pixels
 * @param {number} quality - JPEG quality (0-1)
 * @returns {Promise<string>} Compressed base64 image
 */
export const compressImage = async (base64Image, maxWidth = 1024, quality = 0.85) => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      // Calculate new dimensions
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to compressed JPEG
      const compressedBase64 = canvas.toDataURL('image/jpeg', quality);

      console.log(`📸 Image compressed: ${(base64Image.length / 1024).toFixed(2)}KB → ${(compressedBase64.length / 1024).toFixed(2)}KB`);

      resolve(compressedBase64);
    };

    img.onerror = () => {
      reject(new Error('Failed to load image for compression'));
    };

    img.src = base64Image;
  });
};

/**
 * Convert base64 to Blob
 * @param {string} base64 - Base64 string
 * @returns {Promise<Blob>} Blob object
 */
export const base64ToBlob = async (base64) => {
  const res = await fetch(base64);
  return res.blob();
};

/**
 * Get storage usage estimate
 * @returns {Promise<Object>} Usage info
 */
export const getStorageUsage = async () => {
  if ('storage' in navigator && 'estimate' in navigator.storage) {
    const estimate = await navigator.storage.estimate();

    return {
      used: estimate.usage,
      quota: estimate.quota,
      usedMB: (estimate.usage / (1024 * 1024)).toFixed(2),
      quotaMB: (estimate.quota / (1024 * 1024)).toFixed(2),
      percentUsed: ((estimate.usage / estimate.quota) * 100).toFixed(2)
    };
  }

  return null;
};

const storageService = {
  uploadImage,
  uploadUserPhoto,
  uploadProductImage,
  deleteImage,
  getUserPhoto,
  clearExpiredPhotos,
  listImages,
  compressImage,
  base64ToBlob,
  getStorageUsage
};

export default storageService;
